import { Box, Alert, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useEffect, useState, useMemo } from 'react';
import { DatePicker } from '../../DatePicker';
import { StartTimeGrid } from '../../time-selection/StartTimeGrid';
import { EndTimeSelector } from '../../time-selection/EndTimeSelector';
import { useVenueAvailability, useDateRangeAvailability } from '../../../hooks/useBookings';
import { useEndTimeOptions } from '../../../hooks/useEndTimeOptions';
import type { SlotAvailability } from '@lov/shared';
import { toISTDateString } from '@lov/shared';

interface DateTimeSectionProps {
  venueId?: string;
  basePrice?: number;
  bookingMode?: 'capacity' | 'inventory';
  onComplete: (data: unknown, summary: string) => void;
  registerContinue?: (callback: () => void) => void;
  /** Previously selected values to restore when section is re-opened */
  initialData?: {
    date?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    durationMinutes?: number;
  };
}

export function DateTimeSection({
  venueId,
  basePrice = 0,
  bookingMode,
  onComplete,
  registerContinue,
  initialData,
}: DateTimeSectionProps) {
  const [selectedDate, setSelectedDate] = useState<string | null>(initialData?.date ?? null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(
    initialData?.startTime ?? null
  );
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(
    initialData?.endTime ?? null
  );
  const [durationMinutes, setDurationMinutes] = useState<number>(initialData?.durationMinutes ?? 0);

  // Calculate date range for availability (14 days from today)
  const { startDate, endDate } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    end.setDate(today.getDate() + 13); // 14 days total (0-13)

    const formatDate = (date: Date) => toISTDateString(date);

    return {
      startDate: formatDate(today),
      endDate: formatDate(end),
    };
  }, []);

  // Fetch date range availability for calendar indicators
  const { data: dateRangeData, isLoading: isLoadingDateRange } = useDateRangeAvailability(
    venueId || '',
    startDate,
    endDate
  );

  // Fetch availability when date is selected
  const {
    data: availabilityData,
    isLoading: isLoadingAvailability,
    error: availabilityError,
  } = useVenueAvailability(venueId || '', {
    date: selectedDate || '',
  });

  const slots: SlotAvailability[] = availabilityData?.slots ?? [];

  // Get end time options when start time is selected
  const endTimeOptions = useEndTimeOptions({
    slots: slots,
    selectedStartTime: selectedStartTime,
    basePrice: basePrice,
  });

  const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    // Reset time selections when date changes
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setDurationMinutes(0);
  };

  const handleStartTimeSelect = (time: string) => {
    setSelectedStartTime(time);
    // Reset end time when start time changes
    setSelectedEndTime(null);
    setDurationMinutes(0);
  };

  // In capacity mode, auto-select the first inventory item's venueInventoryId for addons lookup.
  // In inventory mode, the user will choose from the dedicated InventorySelectionSection instead.
  const inventoryId =
    bookingMode === 'capacity' || bookingMode === undefined
      ? availabilityData?.inventoryAvailability?.[0]?.venueInventoryId
      : undefined;

  const handleEndTimeSelect = (endTime: string, duration: number) => {
    setSelectedEndTime(endTime);
    setDurationMinutes(duration);

    // Auto-advance to next section after brief delay for visual feedback
    setTimeout(() => {
      if (selectedDate && selectedStartTime) {
        const data = {
          date: selectedDate,
          startTime: selectedStartTime,
          endTime: endTime,
          durationMinutes: duration,
          bookingMode: availabilityData?.bookingMode,
          ...(inventoryId && { inventoryId }),
        };

        const summary = `${format(new Date(selectedDate), 'PPP')} • ${selectedStartTime} - ${endTime}`;
        onComplete(data, summary);
      }
    }, 300);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleContinue = () => {
    if (!selectedDate || !selectedStartTime || !selectedEndTime) {
      return;
    }

    const data = {
      date: selectedDate,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      durationMinutes,
      bookingMode: availabilityData?.bookingMode,
      ...(inventoryId && { inventoryId }),
    };

    const summary = `${format(new Date(selectedDate), 'PPP')} • ${selectedStartTime} - ${selectedEndTime}`;
    onComplete(data, summary);
  };

  useEffect(() => {
    if (registerContinue) {
      registerContinue(handleContinue);
    }
  }, [registerContinue, handleContinue]);

  if (!venueId) {
    return <Alert severity="error">Venue not selected. Please complete the previous step.</Alert>;
  }

  return (
    <motion.div animate={{ opacity: 1 }} initial={{ opacity: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Date Selection */}
        <DatePicker
          selectedDate={selectedDate}
          onSelectDate={handleDateSelect}
          daysToShow={14}
          dateAvailability={dateRangeData?.dates}
          isLoadingAvailability={isLoadingDateRange}
        />

        {/* Start Time Selection - Show after date selected */}
        {selectedDate && (
          <>
            {isLoadingAvailability ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : availabilityError ? (
              <Alert severity="error">Failed to load availability. Please try again.</Alert>
            ) : slots.length > 0 ? (
              <StartTimeGrid
                slots={slots}
                selectedTime={selectedStartTime}
                onTimeSelect={handleStartTimeSelect}
                maxCapacity={availabilityData?.maxCapacity}
                totalQuantity={availabilityData?.totalQuantity}
              />
            ) : (
              <Alert severity="info">No available time slots for this date.</Alert>
            )}
          </>
        )}

        {/* End Time/Duration Selection - Show after start time selected */}
        {selectedDate && selectedStartTime && endTimeOptions.length > 0 && (
          <EndTimeSelector
            options={endTimeOptions}
            selectedStartTime={selectedStartTime}
            selectedEndTime={selectedEndTime}
            onEndTimeSelect={handleEndTimeSelect}
            bookingMode={availabilityData?.bookingMode ?? bookingMode}
          />
        )}
      </Box>
    </motion.div>
  );
}
